import { createSlice } from "@reduxjs/toolkit";
import { isTokenExpired, getTokenExpiry } from "../../utils/tokenUtils";

const storedUser = localStorage.getItem("user");
let initialUser = null;
let isInitialTokenValid = false;

// Safely parse stored user data
if (storedUser) {
  try {
    initialUser = JSON.parse(storedUser);
    // Check if token exists and is not expired
    isInitialTokenValid = initialUser?.token && !isTokenExpired(initialUser.token);
    
    // If token is expired, clear it from localStorage
    if (initialUser?.token && isTokenExpired(initialUser.token)) {
      localStorage.removeItem("user");
      localStorage.removeItem("refreshToken");
      initialUser = null;
    }
  } catch (error) {
    localStorage.removeItem("user");
    localStorage.removeItem("refreshToken");
  }
}

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: isInitialTokenValid ? initialUser : null,
    isAuthenticated: !!isInitialTokenValid,
    tokenExpiry: isInitialTokenValid && initialUser?.token ? (() => {
      const expiry = getTokenExpiry(initialUser.token);
      return expiry ? expiry.toISOString() : null;
    })() : null,
    keepMeLogin: isInitialTokenValid && initialUser?.keepMeLogin ? initialUser.keepMeLogin : false,
    profileData: isInitialTokenValid && initialUser?.profileData ? initialUser.profileData : null,
    settingsData: isInitialTokenValid && initialUser?.settingsData ? initialUser.settingsData : null,
    lastDataRefresh: isInitialTokenValid && initialUser?.lastDataRefresh ? initialUser.lastDataRefresh : null,
  },
  reducers: {
    login: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.keepMeLogin = action.payload.keepMeLogin || false;
      state.profileData = action.payload.profileData || null;
      state.settingsData = action.payload.settingsData || null;
      state.lastDataRefresh = action.payload.lastDataRefresh || null;
      
      // Calculate token expiry if token exists (store as ISO string for serialization)
      if (action.payload.token) {
        const expiry = getTokenExpiry(action.payload.token);
        state.tokenExpiry = expiry ? expiry.toISOString() : new Date(Date.now() + 60 * 60 * 1000).toISOString();
      }
      
      localStorage.setItem("user", JSON.stringify(action.payload));
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.tokenExpiry = null;
      state.keepMeLogin = false;
      state.profileData = null;
      state.settingsData = null;
      state.lastDataRefresh = null;
      localStorage.removeItem("user");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("keepMeLogin");
    },
    updateToken: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        if (action.payload.token) {
          const expiry = getTokenExpiry(action.payload.token);
          state.tokenExpiry = expiry ? expiry.toISOString() : new Date(Date.now() + 60 * 60 * 1000).toISOString();
        }
        if (action.payload.keepMeLogin !== undefined) {
          state.keepMeLogin = action.payload.keepMeLogin;
        }
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    updateProfileData: (state, action) => {
      state.profileData = action.payload;
      if (state.user) {
        state.user.profileData = action.payload;
        state.user.lastDataRefresh = new Date().toISOString();
        state.lastDataRefresh = new Date().toISOString();
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    updateSettingsData: (state, action) => {
      state.settingsData = action.payload;
      if (state.user) {
        state.user.settingsData = action.payload;
        state.user.lastDataRefresh = new Date().toISOString();
        state.lastDataRefresh = new Date().toISOString();
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    updateUserData: (state, action) => {
      state.profileData = action.payload.profileData || state.profileData;
      state.settingsData = action.payload.settingsData || state.settingsData;
      state.lastDataRefresh = new Date().toISOString();
      if (state.user) {
        state.user.profileData = state.profileData;
        state.user.settingsData = state.settingsData;
        state.user.lastDataRefresh = state.lastDataRefresh;
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    checkTokenExpiry: (state) => {
      if (state.user?.token && isTokenExpired(state.user.token)) {
        // If keepMeLogin is enabled, try to refresh the token instead of logging out
        if (state.keepMeLogin) {
          // This will be handled by the axios interceptor
          return;
        }
        
        state.user = null;
        state.isAuthenticated = false;
        state.tokenExpiry = null;
        state.keepMeLogin = false;
        state.profileData = null;
        state.settingsData = null;
        state.lastDataRefresh = null;
        localStorage.removeItem("user");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("keepMeLogin");
      }
    },
  },
});

export const { login, logout, updateToken, updateProfileData, updateSettingsData, updateUserData, checkTokenExpiry } = authSlice.actions;
export default authSlice.reducer;
